import { Box, Card, Flex, Image, Skeleton, Text } from '@chakra-ui/react';

import { ClampedTextV2 } from '~/components/clamped-text-v2';
import { Rating } from '~/features/experiments/ab_plp_redesign_2025/components/rating';
import type { ProgramReview } from '~/features/program/models/program-review';
import { format, useFormatDate } from '~/hooks/use-format-date';

import { CAPTION_FONT_SIZE, CARD_BORDER_COLOR, TRUSTPILOT_LOGO } from '../constants';
import { getReviewAttribution } from '../utils/get-review-attribution';

const QUOTE_LINE_CLAMP = 3;

/**
 * Line box heights the skeleton has to reserve, resolved from the v2 theme tokens the loaded card
 * uses: `lineHeight='md'` (1.375rem) for the quote, `lineHeight='sm'` (1.25rem) for the "Read More"
 * toggle, the attribution and the date. The toggle's line counts even though the skeleton has no
 * toggle to show — the modal grid stretches every card in a row to the tallest, so a row that loads
 * a clamped review would grow by a line if the placeholder left that space out.
 */
const QUOTE_LINE_HEIGHT = '1.375rem';
const CAPTION_LINE_HEIGHT = '1.25rem';
const SKELETON_BAR_HEIGHT = '14px';

/** A shimmer bar centered in a text line box, so the placeholder occupies the height real text would. */
function SkeletonLine({ height, width }: { height: string; width: string }) {
  return (
    <Flex alignItems='center' height={height}>
      <Skeleton height={SKELETON_BAR_HEIGHT} width={width} />
    </Flex>
  );
}

function CardShell({ children, width }: { children: React.ReactNode; width?: string }) {
  return (
    <Card
      as='article'
      border='1px solid'
      borderColor={CARD_BORDER_COLOR}
      borderRadius='8px'
      boxShadow='none'
      display='flex'
      flexDirection='column'
      flexShrink={0}
      gap={4}
      overflow='hidden'
      padding={4}
      width={width}
    >
      {children}
    </Card>
  );
}

function TrustpilotLogo() {
  return (
    <Image
      alt={TRUSTPILOT_LOGO.alt}
      height={`${TRUSTPILOT_LOGO.height}px`}
      src={TRUSTPILOT_LOGO.src}
      width={`${TRUSTPILOT_LOGO.width}px`}
    />
  );
}

export function ReviewCard({ review, width }: { review: ProgramReview; width?: string }) {
  const formattedDate = useFormatDate({ date: review.createdAt, format: format.monthDayYearShort, forceUTC: true });
  const attribution = getReviewAttribution(review.consumer.name);

  return (
    <CardShell width={width}>
      <Flex alignItems='center' gap={1}>
        <Rating rating={review.stars} />
        <Text color='supportOrange.70' fontSize='sm' fontWeight='500' lineHeight='short'>
          {review.stars}
        </Text>
      </Flex>
      <Box as='blockquote'>
        <ClampedTextV2
          color='supportGray.90'
          fontSize='sm'
          lineHeight='md'
          noOfLines={QUOTE_LINE_CLAMP}
          renderButton={({ isClamped, onToggle }) => (
            <Text
              as='button'
              color='primary.blue'
              fontSize='sm'
              fontWeight='700'
              lineHeight='sm'
              textDecoration='underline'
              width='fit-content'
              onClick={onToggle}
            >
              {isClamped ? 'Read More' : 'Minimize'}
            </Text>
          )}
        >
          <Text
            as='span'
            sx={{
              '&::before': { content: '"\\201C"' },
              '&::after': { content: '"\\201D"' },
            }}
          >
            {review.content?.trim()}
          </Text>
        </ClampedTextV2>
      </Box>
      {attribution && (
        <Text
          as='cite'
          color='supportGray.100'
          fontSize={CAPTION_FONT_SIZE}
          fontStyle='normal'
          fontWeight='600'
          lineHeight='sm'
          noOfLines={1}
          textTransform='capitalize'
        >
          {attribution}
        </Text>
      )}
      <Flex alignItems='center' gap={2} justifyContent='space-between' marginTop='auto'>
        <Text color='supportGray.60' fontSize={CAPTION_FONT_SIZE} lineHeight='sm'>
          {formattedDate}
        </Text>
        <TrustpilotLogo />
      </Flex>
    </CardShell>
  );
}

export function ReviewSkeletonCard({ width }: { width?: string }) {
  return (
    <CardShell width={width}>
      <Flex alignItems='center' gap={1}>
        <Rating rating={5} isLoading />
      </Flex>
      <Box>
        <SkeletonLine height={QUOTE_LINE_HEIGHT} width='100%' />
        <SkeletonLine height={QUOTE_LINE_HEIGHT} width='85%' />
        <SkeletonLine height={QUOTE_LINE_HEIGHT} width='60%' />
        <SkeletonLine height={CAPTION_LINE_HEIGHT} width='72px' />
      </Box>
      <SkeletonLine height={CAPTION_LINE_HEIGHT} width='120px' />
      <Flex alignItems='center' justifyContent='space-between' marginTop='auto'>
        <SkeletonLine height={CAPTION_LINE_HEIGHT} width='80px' />
        <Skeleton height={`${TRUSTPILOT_LOGO.height}px`} width={`${TRUSTPILOT_LOGO.width}px`} />
      </Flex>
    </CardShell>
  );
}
